import { IPopoverProps, Popover } from "@blueprintjs/core"
import { DatePicker, DatePickerProps } from "@blueprintjs/datetime"
import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import customParseFormat from "dayjs/plugin/customParseFormat"
import updateLocale from "dayjs/plugin/updateLocale"
import React, { useState } from "react"
import { withFormController } from "../Molecules/FormController"
import CustomButton, { CustomButtonProps } from "./CustomButton"
dayjs.extend(updateLocale)
dayjs.extend(advancedFormat)
dayjs.extend(customParseFormat)

// Component Name - Custom Date Picker
// Component Used For - Selecting Date
// Author - Malik Bagwala

/*
Props
1. format - the string format in which the date picker will send the data to its parent and expects to recieve date in the same format (default "YYYY-MM-DD")
2. dark - able to set the theme of the date picker will be set throughout its children
3. valueAs - the type of data the DatePicker will output ( default string)
4. closeOnClear - whether to close the popover if a null value is selected
5. onClear - the callback invoked when the user passes a null value or when hits the `Clear` button

Blueprint
Component - DatePicker, CustomPopover

Link to Documentation -
https://blueprintjs.com/docs/#datetime/datepicker
https://blueprintjs.com/docs/#core/components/popover
*/

export interface CustomDatePickerProps
  extends CustomButtonProps,
    Omit<DatePickerProps, "onChange"> {
  /**
   * All the other extra popover props should be specified over here
   */
  popoverProps?: IPopoverProps
  /**
   * All the other custom button props should be specified over here
   */
  buttonProps?: CustomButtonProps
  /**
   * The format string the user wishes to get the date output in
   * @default "YYYY-MM-DD"
   */
  format?: string
  /**
   * The path at which to pull the data from the source json object
   * eg. user.dateOfBirth
   */
  path?: string
  /**
   * The type of value this component should send out
   * if set as an object the component will send out a Date object and a formatted string
   * if none is provided
   * @default "string"
   */
  valueAs?: "string" | "object"
  /**
   * If set to true, the component's popover will close after the user chooses/selects a date
   * @default true
   */
  closeOnSelect?: boolean
  onClear?: (date: any) => {}
  /**
   * If set to true, the component's popover will close after the user clicks the clear button
   * @default true
   */
  closeOnClear?: boolean

  onChange: any
}
dayjs.updateLocale(dayjs.locale(), { invalidDate: "No Date!" })
const CustomDatePicker = ({
  minimal,
  popoverProps,
  buttonProps,
  icon,
  intent,
  text,
  value,
  format,
  path,
  valueAs,
  onChange,
  showActionsBar,
  timePickerProps,
  className,
  minDate,
  maxDate,
  closeOnSelect,
  onClear,
  closeOnClear,
  ...otherProps
}: CustomDatePickerProps) => {
  const formatToObject = (dateText: Date | any) => {
    if (dateText instanceof Date) {
      return dateText
    } else {
      if (dayjs(dateText).toISOString()) {
        return dayjs(dateText).toDate()
      } else {
        const value = dayjs(dateText, format).toISOString()
        return value ? new Date(value) : undefined
      }
    }
  }

  const formatToString = (date: any) => {
    return dayjs(date).format(format)
  }
  const [isOpen, toggleOpen] = useState(false)
  const handleChange = (date: any) => {
    if (valueAs === "string") {
      onChange(path, formatToString(date))
    } else {
      onChange(path, date)
    }
    closeOnSelect && toggleOpen(false)
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => toggleOpen(false)}
      {...popoverProps}
      content={
        <DatePicker
          highlightCurrentDay={true}
          onChange={handleChange}
          value={value ? formatToObject(value) : undefined}
          showActionsBar={showActionsBar}
          timePickerProps={timePickerProps}
          minDate={minDate}
          maxDate={maxDate}
          {...otherProps}
        ></DatePicker>
      }
    >
      <CustomButton
        icon={icon}
        intent={intent}
        text={formatToString(value)}
        className={className}
        {...buttonProps}
        onClick={() => toggleOpen(!isOpen)}
      />
    </Popover>
  )
}

CustomDatePicker.defaultProps = {
  format: "YYYY-MM-DD",
  valueAs: "string",
  minDate: dayjs().subtract(50, "years").toDate(),
  maxDate: dayjs().add(10, "year").toDate(),
  showActionsBar: true,
  closeOnSelect: true,
  closeOnClear: true,
}
export default withFormController(CustomDatePicker)
